<template>
  <section id="agitation-timeline" class="py-16 md:py-24 bg-base-100">
    <div class="max-w-screen-xl mx-auto px-4">
      <!-- Clean section header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-base-content">
          {{ $t("agitationTimeline.heading") }}
        </h2>
        <div class="divider divider-primary w-32 mx-auto mt-6"></div>
        <p class="text-lg text-base-content/80 max-w-2xl mx-auto mt-4">
          {{ $t("agitationTimeline.subtitle") }}
        </p>
      </div>

      <!-- Statistics Cards Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Invisibility Warning Card -->
        <div class="card bg-base-200 shadow-lg hover:shadow-xl transition-shadow">
          <div class="card-body text-center">
            <!-- Icon -->
            <div class="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-error" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5S21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12S9.24 7 12 7 17 9.24 17 12 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12S10.34 15 12 15 15 13.66 15 12 13.66 9 12 9Z" />
                <path d="M2 2L22 22" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
              </svg>
            </div>

            <!-- Statistic -->
            <div class="text-5xl font-black text-error mb-2">
              {{ $t("agitationTimeline.stats.research.percentage") }}
            </div>
            <p class="text-base-content/80 mb-4">{{ $t("agitationTimeline.stats.research.description") }}</p>

            <!-- Progress bar -->
            <div class="w-full bg-base-300 rounded-full h-2 mb-4">
              <div class="bg-error h-2 rounded-full" style="width: 82%"></div>
            </div>

            <p class="text-sm text-base-content/70">
              {{ $t("agitationTimeline.stats.research.impact") }}
            </p>
          </div>
        </div>

        <!-- Quality Warning Card -->
        <div class="card bg-base-200 shadow-lg hover:shadow-xl transition-shadow">
          <div class="card-body text-center">
            <!-- Icon -->
            <div class="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-warning" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clip-rule="evenodd" />
              </svg>
            </div>

            <!-- Statistic -->
            <div class="text-5xl font-black text-warning mb-2">
              {{ $t("agitationTimeline.stats.credibility.percentage") }}
            </div>
            <p class="text-base-content/80 mb-4">{{ $t("agitationTimeline.stats.credibility.description") }}</p>

            <!-- Visual rejection indicator -->
            <div class="flex justify-center gap-1 mb-4">
              <div class="w-3 h-3 bg-error rounded-full"></div>
              <div class="w-3 h-3 bg-error rounded-full"></div>
              <div class="w-3 h-3 bg-error rounded-full"></div>
              <div class="w-3 h-3 bg-base-300 rounded-full"></div>
            </div>

            <p class="text-sm text-base-content/70">
              {{ $t("agitationTimeline.stats.credibility.impact") }}
            </p>
          </div>
        </div>

        <!-- Time Urgency Warning Card -->
        <div class="card bg-base-200 shadow-lg hover:shadow-xl transition-shadow">
          <div class="card-body text-center">
            <!-- Icon -->
            <div class="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-error" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 4C16.4 4 20 7.6 20 12S16.4 20 12 20 4 16.4 4 12 7.6 4 12 4M12.5 7V12.25L17 14.92L16.25 16.15L11 13V7H12.5Z" />
              </svg>
            </div>

            <!-- Statistic -->
            <div class="text-4xl font-black text-error mb-2">{{ $t("agitationTimeline.stats.impression.time") }}</div>
            <p class="text-base-content/80 mb-4">{{ $t("agitationTimeline.stats.impression.description") }}</p>

            <!-- Time visualization -->
            <div class="bg-error/10 rounded-lg p-3 mb-4">
              <span class="text-error font-bold text-sm">{{ $t("agitationTimeline.stats.impression.visual") }}</span>
            </div>

            <p class="text-sm text-base-content/70">
              {{ $t("agitationTimeline.stats.impression.impact") }}
            </p>
          </div>
        </div>
      </div>

      <!-- Pain Acknowledgment Hook -->
      <div class="text-center mt-16">
        <div class="card bg-base-100 shadow-lg max-w-4xl mx-auto">
          <div class="card-body">
            <h3 class="text-3xl font-bold text-base-content mb-4 leading-tight">
              {{ $t("solutionBridge.hookQuestion") }}
            </h3>
            <p class="text-lg text-base-content/80 leading-relaxed">
              {{ $t("solutionBridge.painAcknowledgment") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
// No additional logic needed for this component
</script>

<style scoped>
.timeline-item {
  position: relative;
}

.timeline-item::before {
  content: "";
  position: absolute;
  left: 24px;
  top: 60px;
  width: 2px;
  height: calc(100% + 32px);
  background: oklch(var(--b3));
}

.timeline-item:last-child::before {
  display: none;
}

/* Premium animations */
@keyframes luxuryFadeInUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes luxuryScale {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes shimmer {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 100% 0;
  }
}

.group:hover .luxury-shimmer {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out;
}

/* Sophisticated hover effects */
.luxury-card {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.luxury-card:hover {
  transform: translateY(-2px);
}

/* Premium number gradient animation */
.premium-number {
  background-size: 200% 200%;
  animation: gradient-shift 3s ease-in-out infinite alternate;
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
}
</style>
